import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {EffectiveRole, User} from "../../../../_models/user";
import {NotificationService} from "../../../../_services/notification.service";
import {Notification} from "../../../../_models/notification";
import {Router} from "@angular/router";
import {GlobalStore} from "../../../../global.store";
import {Subject, takeUntil} from "rxjs";
import {AuthService} from "../../../../_services/auth.service";
import {SidebarService} from '../sidebar/sidebar.service';
import {Company} from '../../../../_models/company';

@Component({
    selector: 'app-sub-header',
    templateUrl: './sub-header.component.html',
    styleUrls: ['./sub-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SubHeaderComponent implements OnInit, OnDestroy {
  @ViewChild('notificationContainer') notificationContainer: ElementRef
  @Input() title: string[];

  isSelectUnreadNotif: boolean = false;

  userImg!: string;
  isOpenMenu: boolean = false;
  isNotifSettingsOpen = false;
  isOpenNotifications = false;
  user: User;
  currentCompany: Company | null = null;
  filteredNotificationsList: Notification[] = [];
  isDashboard = false;
  isLoadingMoreNotifications = false;
  hasMoreNotifications = true;

  private readonly destroy$ = new Subject<void>()
  private readonly notificationsBeingMarkedAsRead = new Set<number>();
  constructor(readonly globalStore: GlobalStore,
              public notificationService: NotificationService,
              public router: Router,
              public authService: AuthService,
              readonly sidebarService: SidebarService,
              private cdRef: ChangeDetectorRef) {
    this.globalStore.currentUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      this.user = user;
      this.cdRef.markForCheck();
    });
    this.globalStore.company$.pipe(takeUntil(this.destroy$)).subscribe((company) => {
      this.currentCompany = company;
      this.cdRef.markForCheck();
    });
    this.isDashboard = this.router.routerState.snapshot.url.includes('dashboard')
  }

  ngOnInit() {
    this.notificationService.notificationsChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isOpenNotifications) {
          this.filterNotifications();
          return;
        }

        this.cdRef.markForCheck();
      });

    this.getUnreadNotificationsCount()
  }

  openSidebar() {
    this.sidebarService.setMenuState(true)
    document.querySelector('.sidebar-wrapper').setAttribute('isOpenMenu', 'true')
  }

  openProfileDropDown() {
    this.isOpenMenu = !this.isOpenMenu;
  }

  get myProfileLink(): any[] {
    if (this.user?.effectiveRole === EffectiveRole.ROLE_SUPER_ADMIN) {
      if (this.currentCompany?.id && this.user?.id) {
        return ['/company', this.currentCompany.id, 'users', this.user.id];
      }

      return ['/profile'];
    }

    const activeCompanyId = this.currentCompany?.id ?? this.user?.companies?.at(0)?.id;
    if (activeCompanyId && this.user?.id) {
      return ['/company', activeCompanyId, 'users', this.user.id];
    }

    return ['/profile'];
  }

  get accountSettingsLink(): any[] {
    if (this.user?.effectiveRole === EffectiveRole.ROLE_SUPER_ADMIN) {
      return this.user?.id ? ['/users', this.user.id] : ['/profile'];
    }

    const activeCompanyId = this.currentCompany?.id ?? this.user?.companies?.at(0)?.id;
    return activeCompanyId ? ['/company', activeCompanyId, 'account'] : ['/profile'];
  }

  toggleNotifications() {
    this.isOpenNotifications = !this.isOpenNotifications;
    if (this.isOpenNotifications) {
      this.ensureNotificationsLoaded();
    }
    this.filterNotifications();
  }

  closeNotifications() {
    this.isOpenNotifications = false;
    this.markAsReadNotif();
  }

  markAsReadNotif(){
    const readNotificationIds = this.notificationService.notificationsList
      .filter(notification => notification.wasRead === true && notification.delivered === false)
      .map(notification => notification.id);

    this.notificationService.markNotificationsAsRead(readNotificationIds);
    this.filterNotifications();
  }

  notifVisible(notif: Notification){
    if (!notif.delivered && !this.notificationsBeingMarkedAsRead.has(notif.id)) {
      this.notificationsBeingMarkedAsRead.add(notif.id);

      this.notificationService.notificationDelivered(notif.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.notificationService.markNotificationsAsRead([notif.id]);
            this.filterNotifications();
          },
          complete: () => {
            this.notificationsBeingMarkedAsRead.delete(notif.id);
          },
          error: () => {
            this.notificationsBeingMarkedAsRead.delete(notif.id);
            this.getUnreadNotificationsCount();
          }
        });
    }
  }

  loadMoreNotifications() {
    if (
      this.isLoadingMoreNotifications ||
      !this.hasMoreNotifications ||
      this.notificationService.notificationsList.length === 0
    ) {
      return;
    }

    const lastNotification =
      this.notificationService.notificationsList[
        this.notificationService.notificationsList.length - 1
      ];

    if (!lastNotification?.id) {
      this.hasMoreNotifications = false;
      return;
    }

    this.isLoadingMoreNotifications = true;

    this.notificationService.getNotifications(lastNotification.id).subscribe({
      next: (data) => {
        if (!data.length) {
          this.hasMoreNotifications = false;
          return;
        }

        const existingIds = new Set(
          this.notificationService.notificationsList.map(notification => notification.id)
        );

        const nextNotifications = data.filter(
          notification => !existingIds.has(notification.id)
        );

        if (!nextNotifications.length) {
          this.hasMoreNotifications = false;
          return;
        }

        this.notificationService.notificationsList = [
          ...this.notificationService.notificationsList,
          ...nextNotifications
        ].sort((left, right) => right.createdAt - left.createdAt);
        this.filterNotifications();
      },
      error: () => {
        this.cdRef.markForCheck();
      },
      complete: () => {
        this.isLoadingMoreNotifications = false;
        this.cdRef.markForCheck();
      }
    });
  }

  isLoadMoreTrigger(index: number): boolean {
    const triggerIndex = Math.max(this.filteredNotificationsList.length - 2, 0);
    return this.hasMoreNotifications && index === triggerIndex;
  }

  openNotification(notification: Notification) {
    this.router.navigateByUrl(notification.link);
    this.closeNotifications();
  }


  getUnreadNotificationsCount() {
    this.notificationService.refreshUnreadCount((count) => {
      if (count > 0 && this.notificationService.notificationsList.length === 0) {
        this.ensureNotificationsLoaded();
      }
      this.cdRef.markForCheck();
    });
  }

  private ensureNotificationsLoaded() {
    if (this.isLoadingMoreNotifications) {
      return;
    }

    this.isLoadingMoreNotifications = true;
    this.hasMoreNotifications = true;

    this.notificationService.refreshNotifications().subscribe({
      next: (notifications) => {
        this.notificationService.replaceNotificationsList(notifications);

        if (!notifications.length) {
          this.hasMoreNotifications = false;
        }
      },
      error: () => {
        this.cdRef.markForCheck();
      },
      complete: () => {
        this.isLoadingMoreNotifications = false;
        this.cdRef.markForCheck();
      }
    });
  }

  filterNotifications() {
    this.filteredNotificationsList = this.isSelectUnreadNotif
      ? this.notificationService.notificationsList.filter(notif => notif.delivered === false)
      : [...this.notificationService.notificationsList];

    this.cdRef.markForCheck();
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }

  protected readonly EffectiveRole = EffectiveRole;
}
